import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import EnhancedPaymentMethodsForm from '../PaymentMethodsPage/PaymentMethodsForm/PaymentMethodsForm';
import { ensureCurrentUser } from '../../util/data';
import { createStripeSetupIntent } from '../PaymentMethodsPage/PaymentMethodsPage.duck';
import {
  getClientSecret,
  getDefaultPaymentMethod,
  getPaymentParams,
  getUserDetails,
} from '../../util/userDataExtraction';
import { Modal } from '../../components';
import { showErrorToast } from '../../util/toast';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { paymentMethodsSelector, savePaymentMethod } from '../../ducks/paymentMethods.duck';
import {
  createStripePaymentIntent,
  createSubscriptionForListing,
  handleCardSetup,
  handleCardSetupErrorSelector,
  initiateSubscription,
  updateSubscription,
} from '../../ducks/stripe.duck';
import {
  ReferralStatus,
  SendgridEmailTemplate,
  SubscriptionPlanName,
  UserType,
} from '../../util/enums';
import { showQuoteForm } from '../../util/subscriptionHelpers';
import { fetchCurrentUser } from '../../ducks/user.duck';
import SubscriptionAgencyForm from './SubscriptionAgencyForm/SubscriptionAgencyForm';
import {
  sendAgencyPlusData,
  sendAgencyPlusDataSelector,
  updateSubscriptionType,
} from './SubscriptionPage.duck';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';

import css from './SubscriptionPage.module.css';
import { useConfiguration } from '../../context/configurationContext';
import { convertToCents } from '../../util/genericHelpers';
import { updateReferrerProfile } from '../../util/helperFunction';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';

const SubscriptionPaymentModal = ({
  isOpen,
  currentUser,
  selectedPlanId,
  isEditListingSubscriptionModal,
  selectedPlanName,
  setPaymentMethodModalState,
  subscriptionId,
  subscriptionType, //Name of the subscription plan
  shouldUpdate,
  userType,
  setIsConfirmationModalOpen = () => {},
  authorId,
  listingId,
  isListingPage = false,
  fromSectionUnlockable = false,
  paymentModalState,
  referrerId,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();

  const { firstName, lastName, email } = !!currentUser?.id && getUserDetails(currentUser);
  const handleCardSetupError = useSelector(handleCardSetupErrorSelector);
  const { sendAgencyPlusDataInProgress } = useSelector(sendAgencyPlusDataSelector);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quoteStep, setQuoteStep] = useState('payment');
  const [quoteParams, setQuoteParams] = useState(null);

  const isBrand = userType === UserType.BRAND;

  const renderQuoteForm = isBrand && quoteStep === 'payment';

  useEffect(() => {
    if (renderQuoteForm && selectedPlanName !== SubscriptionPlanName.NIL_PREMIER) {
      setQuoteStep('quote');
    }
  }, [userType]);

  const {
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
  } = useSelector(paymentMethodsSelector);

  const hasDefaultPaymentMethod = currentUser?.id && getDefaultPaymentMethod(currentUser);

  // HandleSubmit
  async function handleSubmit(params) {
    setIsSubmitting(true);

    try {
      const ensuredCurrentUser = ensureCurrentUser(currentUser);
      const currentUserId = ensuredCurrentUser?.id?.uuid;
      const stripeCustomer = ensuredCurrentUser.stripeCustomer;
      const { stripe, card, formValues } = params;
      const customerId = stripeCustomer?.attributes?.stripeCustomerId;
      let paymentMethodId = stripeCustomer?.defaultPaymentMethod?.attributes?.stripePaymentMethodId;
      const referralUserParams = {
        referrerId,
        currentUserId,
        status: ReferralStatus.PURCHASED,
      };

      if (!stripeCustomer) {
        const setupIntent = await dispatch(createStripeSetupIntent(params));
        const setupIntentClientSecret = getClientSecret(setupIntent);
        const paymentParams = getPaymentParams(currentUser, formValues);

        // Handle card setup
        const stripeParams = { stripe, card, setupIntentClientSecret, paymentParams };
        const result = await dispatch(handleCardSetup(stripeParams));
        const newPaymentMethod = result.setupIntent.payment_method;

        paymentMethodId = newPaymentMethod?.id;
        // Save the new payment method
        await dispatch(savePaymentMethod(stripeCustomer, newPaymentMethod));
      }

      if (fromSectionUnlockable) {
        const paymentPayload = {
          customerId: customerId,
          amount: convertToCents(paymentModalState?.priceAmount),
          currency: config?.currency,
          paymentMethodId: paymentMethodId,
        };

        await dispatch(createStripePaymentIntent(paymentPayload, paymentModalState));
        await dispatch(fetchCurrentUser());
        if (referrerId) {
          updateReferrerProfile(referralUserParams);
        }
      } else {
        // Create Stripe setup intent

        if (isListingPage) {
          await dispatch(
            createSubscriptionForListing({
              listingId,
              authorId,
              priceId: selectedPlanId,
              authorId,
              listingId,
              isListingPage,
              paymentMethodId,
            })
          );
          if (referrerId) {
            updateReferrerProfile(referralUserParams);
          }
        } else {
          // Update or initiate subscription based on the shouldUpdate flag
          const subscriptionAction = shouldUpdate
            ? updateSubscription({ subscriptionId, newPriceId: selectedPlanId })
            : initiateSubscription({
                priceId: selectedPlanId,
                isOneOff: showQuoteForm(selectedPlanName),
                selectedPlanName,
                paymentMethodId,
              });
          await dispatch(subscriptionAction);
          if (referrerId) {
            updateReferrerProfile(referralUserParams);
          }
        }
        dispatch(fetchCurrentUser());
        dispatch(
          updateProfile({
            publicData: {
              subscriptionType,
            },
          })
        );
        dispatch(
          updateSubscriptionType({
            subscriptionType,
            authorId: ensuredCurrentUser?.id?.uuid,
          })
        );
      }

      // Open confirmation modal
      setIsConfirmationModalOpen();
    } catch (error) {
      setIsSubmitting(false);
      showErrorToast(error.message);
    } finally {
      // Close the modal and reset the submitting state
      setIsSubmitting(false);
      setPaymentMethodModalState(false);
    }
  }

  const renderFormContent = (
    <EnhancedPaymentMethodsForm
      className={css.paymentForm}
      formId="PaymentMethodsForm"
      initialValues={{ name: `${firstName} ${lastName}` }}
      onSubmit={handleSubmit}
      handleRemovePaymentMethod={{}}
      hasDefaultPaymentMethod={hasDefaultPaymentMethod}
      addPaymentMethodError={addPaymentMethodError}
      deletePaymentMethodError={deletePaymentMethodError}
      createStripeCustomerError={createStripeCustomerError}
      handleCardSetupError={handleCardSetupError}
      inProgress={isSubmitting}
      idSubscriptionPage={true}
      paymentMethodModalState={isOpen}
      showQuoteForm={quoteParams}
      onGoBack={() => {
        setQuoteParams(quoteParams);
        setQuoteStep('quote');
      }}
      saveActionMessage={
        fromSectionUnlockable ? (
          <FormattedMessage id="SubscriptionPage.createIntentBtn" />
        ) : (
          <FormattedMessage id="SubscriptionPage.saveActionMessage" />
        )
      }
    />
  );

  const handleAgencyFormSubmit = async values => {
    try {
      await dispatch(
        sendAgencyPlusData({
          templateId: SendgridEmailTemplate.AGENCY_PLUS,
          recipientEmail: values?.email, // change it to admin or platform email later
          templateData: {
            ...values,
          },
        })
      );
    } catch (error) {
      throw error;
    } finally {
      setPaymentMethodModalState(false);
    }
  };
  return (
    <div className={css.subscriptionPaymentFormModal}>
      <Modal
        id="SubscriptionPaymentFormModal"
        isOpen={isOpen}
        onClose={() => {
          setPaymentMethodModalState(false);
          setQuoteStep('quote');
          setQuoteParams(null);
        }}
        usePortal
        contentClassName={css.modalContent}
        onManageDisableScrolling={(componentId, disableScrolling) =>
          dispatch(manageDisableScrolling(componentId, disableScrolling))
        }
        modalTitle={<FormattedMessage id="SubscriptionPaymentFormModal.paymentCardDetails" />}
        isEditListingSubscriptionModal={isEditListingSubscriptionModal}
      >
        {selectedPlanName === SubscriptionPlanName.AGENCY_PLUS ? (
          <SubscriptionAgencyForm
            inProgress={sendAgencyPlusDataInProgress}
            onSubmit={handleAgencyFormSubmit}
            initialValues={{ email }}
          />
        ) : (
          renderFormContent
        )}
      </Modal>
    </div>
  );
};

export default SubscriptionPaymentModal;
